import { ZERO } from '@emberswap/emberswap-sdk2/dist/constants/numbers'
import { Amount } from '@emberswap/emberswap-sdk2/dist/entities/Amount'
import { Price } from '@emberswap/emberswap-sdk2/dist/entities/Price'
import { Type } from '@emberswap/emberswap-sdk2/dist/types/Type'
import { FC, ReactNode, useMemo } from 'react'
import { Address } from 'viem'

import { shortenAddress } from '../../functions'
import { useTokenAmountDollarValues } from '../../hooks/useTokenAmountDollarValues'
import { Currency } from '../Currency'
import { Dialog } from '../Dialog'
import { ChevronDownIcon } from '../Icons'
import { AddressToEnsResolver } from '../Layout/Account/AddressToEnsResolver'
import { List } from '../List'
import { Rate } from '../Rate'

interface SwapReviewModalBase {
  chainId: number | undefined
  input0: Amount<Type> | undefined
  input1: Amount<Type> | undefined
  open: boolean
  setOpen(open: boolean): void
  children: ReactNode
  recipient?: string
}

export const SwapReviewModalBase: FC<SwapReviewModalBase> = ({ chainId, children, input0, input1, open, setOpen, recipient }) => {
  const [value0, value1] = useTokenAmountDollarValues({
    chainId,
    amounts: [input0, input1],
  })

  const price = useMemo(() => {
    if (!input0 || !input1) return undefined
    if (!input1.greaterThan(ZERO)) return undefined
    return new Price({ baseAmount: input0, quoteAmount: input1 })
  }, [input0, input1])

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <Dialog.Content className="max-w-sm !pb-4">
        <Dialog.Header border={false} title="Confirm Swap" onClose={() => setOpen(false)} />
        <div className="!my-0 grid grid-cols-12 items-center">
          <div className="relative flex flex-col col-span-12 gap-1 p-2 border sm:p-4 rounded-2xl bg-slate-700/40 border-slate-200/5">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-between w-full gap-2">
                <h3 className="font-medium truncate text-slate-50">{input0?.toSignificant(6)} </h3>
                <div className="flex items-center justify-end gap-2 text-right">
                  {input0 && (
                    <div className="w-5 h-5">
                      <Currency.Icon currency={input0.currency} width={20} height={20} disableLink />
                    </div>
                  )}
                  <h3 className="font-medium text-right text-slate-50">
                    {input0?.currency.symbol}
                  </h3>
                </div>
              </div>
            </div>
            <p className="text-sm font-medium text-slate-500">
              {value0 ? `$${value0.toFixed(2)}` : '-'}
            </p>
          </div>
          <div className="flex items-center justify-center col-span-12 -mt-2.5 -mb-2.5">
            <div className="p-0.5 bg-slate-700 border-2 border-slate-800 ring-1 ring-slate-200/5 z-10 rounded-full">
              <ChevronDownIcon width={18} height={18} className="text-slate-200" />
            </div>
          </div>
          <div className="flex flex-col col-span-12 gap-1 p-2 border sm:p-4 rounded-2xl bg-slate-700/40 border-slate-200/5">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-between w-full gap-2">
                <h3 className="font-medium truncate text-slate-50 ">
                  {input1?.toSignificant(6)}
                </h3>
                <div className="flex items-center justify-end gap-2 text-right">
                  {input1 && (
                    <div className="w-5 h-5">
                      <Currency.Icon currency={input1.currency} width={20} height={20} />
                    </div>
                  )}
                  <h3 className="font-medium text-right text-slate-50 ">
                    {input1?.currency.symbol}
                  </h3>
                </div>
              </div>
            </div>
            <p className="text-sm font-medium text-slate-500">
              {value1 ? `$${value1.toFixed(2)}` : ''}
            </p>
          </div>
        </div>
        <div className="flex justify-center p-4">
          <Rate price={price}>
            {({ toggleInvert, content, usdPrice }) => (
              <button
                onClick={() => toggleInvert()}

                className="flex items-center gap-1 text-sm font-semibold text-slate-100"
              >
                {content}{' '}
                {usdPrice && <span className="font-normal text-slate-300">(${usdPrice})</span>}
              </button>
            )}
          </Rate>
        </div>
        {recipient && (
          <List className="!py-2">
            <List.Control>
              <List.KeyValue title="Recipient">
                <AddressToEnsResolver address={recipient as Address}>
                  {({ loading, ENSName }) => {
                    return(
                    <a
                      target="_blank"
                      href={/* Chain.accountUrl(chainId, recipient) ??*/ '#'}
                      className="flex items-center gap-2 cursor-pointer text-blue"
                      rel="noreferrer"
                    >
                      {loading || !ENSName ? shortenAddress(recipient) : ENSName}
                    </a>
                    ) 
                  }}
                </AddressToEnsResolver>
              </List.KeyValue>
            </List.Control>
          </List>
        )}
        {children}
      </Dialog.Content>
    </Dialog>
  )
}
